import { UniqueValidationId } from "../../database/validations/keys";
import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCartKey,
  ShopifyCartLineItemKey,
  ShopifyCartLineItemKeyUniquenessKey,
  ShopifyCartLineItemToCartKey,
  ShopifyCartLineItemToProductKey,
  ShopifyCartLineItemToProductVariantKey,
  ShopifyCartLineItemToShopKey,
  ShopifyProductKey,
  ShopifyProductVariantKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCartLineItemKey,
  defaultFilterableApiIdentifiers: ["key"],
  "2022-01": {
    name: "Shopify Cart Line Item",
    fields: [
      {
        name: "Properties",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "properties" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity" } },
        configuration: {
          decimals: 0,
        },
      },
      {
        name: "Key",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "key" } },
        validations: {
          [ShopifyCartLineItemKeyUniquenessKey]: {
            key: ShopifyCartLineItemKeyUniquenessKey,
            specID: UniqueValidationId,
            configuration: {
              values: {
                scopeByField: ShopifyCartLineItemToCartKey,
              },
            },
          },
        },
      },
      {
        name: "Discounts",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discounts" } },
      },
      {
        name: "Discounted Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discounted_price" } },
      },
      {
        name: "Gift Card",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "gift_card" } },
      },
      {
        name: "Grams",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "grams" } },
      },
      {
        name: "Line Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "line_price" } },
      },
      {
        name: "Original Line Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "original_line_price" } },
      },
      {
        name: "Original Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "original_price" } },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price" } },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "sku" } },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "taxable" } },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "title" } },
      },
      {
        name: "Total Discount",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_discount" } },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "vendor" } },
      },
      {
        name: "Discounted Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discounted_price_set" } },
      },
      {
        name: "Line Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "line_price_set" } },
      },
      {
        name: "Original Line Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "original_line_price_set" } },
      },
      {
        name: "Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price_set" } },
      },
      {
        name: "Total Discount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_discount_set" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCartLineItemToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
      {
        name: "Cart",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCartLineItemToCartKey,
        configuration: {
          relatedModelKey: ShopifyCartKey,
        },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCartLineItemToProductVariantKey,
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_id" } },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCartLineItemToProductKey,
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "product_id" } },
      },
    ],
    syncConfig: null,
  },
  "2025-04": {
    fields: [
      {
        name: "Original Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "original_price", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "price", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "sku", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "taxable", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "title", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Total Discount",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "total_discount", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "vendor", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Discounted Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "discounted_price_set", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Line Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "line_price_set", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Original Line Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "original_line_price_set", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "price_set", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Total Discount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "total_discount_set", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCartLineItemToProductVariantKey,
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "variant_id", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCartLineItemToProductKey,
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "product_id", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Gift Card",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "gift_card", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Grams",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "grams", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Line Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "line_price", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Original Line Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "original_line_price", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Discounted Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "discounted_price", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Properties",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "properties", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "quantity", availableInWebhook: ["carts/update", "carts/create"] },
        },
        configuration: {
          decimals: 0,
        },
      },
      {
        name: "Key",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "key", availableInWebhook: ["carts/update", "carts/create"] },
        },
        validations: {
          [ShopifyCartLineItemKeyUniquenessKey]: {
            key: ShopifyCartLineItemKeyUniquenessKey,
            specID: UniqueValidationId,
            configuration: {
              values: {
                scopeByField: ShopifyCartLineItemToCartKey,
              },
            },
          },
        },
      },
      {
        name: "Discounts",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "discounts", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
    ],
  },
};

export const ShopifyCartLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
